import { DATA_DROP_LINE_INDEX, TIMELINE_DROP_ZONE_ID } from 'consts'
import { IMediaItem } from 'types/media'

export type TDropHitTarget =
  | { kind: 'line'; lineIndex: number }
  | { kind: 'gap'; afterLineIndex: number }

export const getDistinctZIndicesDescending = (
  mediaItems: IMediaItem[]
): number[] => {
  const unique = [...new Set(mediaItems.map(item => item.zIndex))]
  return unique.sort((a, b) => b - a)
}

export const zIndexToVisualRowRank = (
  zIndex: number,
  orderedZsDescending: number[]
): number => {
  if (orderedZsDescending.length === 0) {
    return 0
  }
  const rank = orderedZsDescending.indexOf(zIndex)
  return rank === -1 ? 0 : rank
}

export const visualRowRankToZIndex = (
  lineIndex: number,
  rowCount: number
): number => {
  if (rowCount <= 0) {
    return 0
  }
  return rowCount - 1 - lineIndex
}

// Pixels of "near the top/bottom edge of a line" that should resolve as a
// gap target. The actual `BetweenLinesGap` strip is only ~7px tall, which is
// nearly impossible to hit with the OS-files drag's lower-frequency events.
// We extend the gap hit zone into each adjacent line's edge.
const GAP_EDGE_MARGIN_PX = 8

export const resolveDropTargetFromCursor = (
  clientY: number
): TDropHitTarget | null => {
  const dropZone = document.getElementById(TIMELINE_DROP_ZONE_ID)
  if (!dropZone) return null

  const gapEls = dropZone.querySelectorAll<HTMLElement>(
    '[data-gap-after-line-index]'
  )
  let topGapTop = Infinity
  for (const el of Array.from(gapEls)) {
    const r = el.getBoundingClientRect()
    if (r.top < topGapTop) topGapTop = r.top
    if (clientY >= r.top && clientY <= r.bottom) {
      return {
        kind: 'gap',
        afterLineIndex: Number(el.dataset.gapAfterLineIndex)
      }
    }
  }

  // Cursor above the top-most gap → treat as the top gap (afterLineIndex: -1)
  // so a sidebar / OS-files drop here creates a new top row.
  if (gapEls.length > 0 && clientY < topGapTop) {
    return { kind: 'gap', afterLineIndex: -1 }
  }

  const lineEls = dropZone.querySelectorAll<HTMLElement>(
    `[${DATA_DROP_LINE_INDEX}]`
  )
  let bottomMostLineBottom = -Infinity
  let bottomMostLineIndex = -1
  for (const el of Array.from(lineEls)) {
    const r = el.getBoundingClientRect()
    const lineIndex = Number(el.dataset.dropLineIndex)
    if (r.bottom > bottomMostLineBottom) {
      bottomMostLineBottom = r.bottom
      bottomMostLineIndex = lineIndex
    }
    if (clientY >= r.top && clientY <= r.bottom) {
      // Treat the line's top/bottom edge band as a gap target so users have
      // a wider zone to hit than the 7px `BetweenLinesGap` itself.
      if (clientY - r.top < GAP_EDGE_MARGIN_PX) {
        return { kind: 'gap', afterLineIndex: lineIndex - 1 }
      }
      if (r.bottom - clientY < GAP_EDGE_MARGIN_PX) {
        return { kind: 'gap', afterLineIndex: lineIndex }
      }
      return { kind: 'line', lineIndex }
    }
  }

  // Cursor below all lines → treat as the bottom gap so a drop creates a new
  // row beneath the existing ones.
  if (bottomMostLineIndex >= 0 && clientY > bottomMostLineBottom) {
    return { kind: 'gap', afterLineIndex: bottomMostLineIndex }
  }

  return null
}

export const resolveTargetZIndex = (
  target: TDropHitTarget | null,
  existingMediaItems: IMediaItem[]
): number => {
  const orderedZs = getDistinctZIndicesDescending(existingMediaItems)
  const rowCount = orderedZs.length

  if (!target) return rowCount > 0 ? orderedZs[rowCount - 1] : 0

  if (target.kind === 'line') {
    // Use the actual zIndex at this visual rank, not the normalized formula
    // `rowCount - 1 - lineIndex`. The store's zIndex values aren't always
    // normalized (initial project load, sidebar drops, etc.), and a
    // mismatched targetZIndex makes downstream filters (preview cascade,
    // `adjustTimelineForNewItems`) miss every existing item — so the drop
    // ends up creating a new line instead of inserting into the hovered one.
    if (rowCount === 0) return 0
    const clampedRank = Math.max(0, Math.min(target.lineIndex, rowCount - 1))
    return orderedZs[clampedRank]
  }

  // Gap → preview-only zIndex (`maxZ + 1`). The actual placement zIndex for
  // a gap drop is computed by `resolveGapZIndex` in the media thunk, which
  // also bumps existing items if the new row needs to slot mid-stack.
  const maxZ = rowCount > 0 ? orderedZs[0] : -1
  return maxZ + 1
}

// Resolves the final zIndex for a gap drop and computes the existing-items
// zIndex bumps needed to make room for a new row. `afterLineIndex` is the
// visual row position; -1 = above the topmost row, N-1 = below the last row,
// anything in between = mid-stack.
//
// Mid-stack and bottom drops both bump rows 0..afterLineIndex up by 1 so the
// store's zIndex values stay contiguous. Non-contiguous zIndex breaks
// `resolveTargetZIndex` and the `DropIndicator` row lookup downstream.
export const resolveGapZIndex = (
  mediaItems: IMediaItem[],
  afterLineIndex: number
): { lineZIndex: number; bumpedMediaItems: IMediaItem[] | null } => {
  const orderedZs = getDistinctZIndicesDescending(mediaItems)
  const rowCount = orderedZs.length

  if (rowCount === 0 || afterLineIndex === -1) {
    return {
      lineZIndex: rowCount > 0 ? orderedZs[0] + 1 : 0,
      bumpedMediaItems: null
    }
  }

  // Bump rows 0..afterLineIndex up by 1; the freed slot (the old zIndex of
  // the row above the gap) becomes the new row's zIndex. For
  // afterLineIndex === N-1 this bumps every row, leaving the new row at the
  // old bottom-row zIndex (visually below all of them).
  const clampedAfter = Math.min(afterLineIndex, rowCount - 1)
  const thresholdZ = orderedZs[clampedAfter]
  const bumpedMediaItems = mediaItems.map(item =>
    item.zIndex >= thresholdZ ? { ...item, zIndex: item.zIndex + 1 } : item
  )
  return { lineZIndex: thresholdZ, bumpedMediaItems }
}

export const getDropLineKey = ({
  isGapDrop,
  insertTime,
  targetGapAfterLineIndex,
  targetZIndex
}: {
  isGapDrop: boolean
  insertTime: number
  targetGapAfterLineIndex?: number
  targetZIndex: number
}) => {
  const dropKey = isGapDrop
    ? `gap:${insertTime}:${targetGapAfterLineIndex}`
    : `line:${insertTime}:${targetZIndex}`

  return dropKey
}
